/**
 * Inline stroke icons on a 24px grid.
 *
 * Drawn rather than pulled from an icon package: the set is small, they
 * inherit `currentColor` so they follow the theme, and the bundle stays free
 * of a dependency for nine glyphs.
 */

interface IconProps {
  size?: number;
  className?: string;
}

function Svg({ size = 16, className, children }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export const IconGrid = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </Svg>
);

export const IconUsers = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="9" cy="8" r="3" />
    <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
    <circle cx="18" cy="9" r="2.5" />
    <path d="M16 20c0-2.4 1.3-4.3 3.2-5" />
  </Svg>
);

export const IconUserCog = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="9" cy="8" r="3" />
    <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
    <path d="M17 11h4M19 9v4" />
  </Svg>
);

export const IconBuilding = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 21V8l9-5 9 5v13" />
    <path d="M9 21v-7h6v7" />
  </Svg>
);

export const IconBuildings = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 21V9l6-3v15" />
    <path d="M9 21V6l6-3v18" />
    <path d="M15 21V11l6 3v7" />
    <path d="M2 21h20" />
  </Svg>
);

export const IconMoon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M21 13A9 9 0 1111 3a7 7 0 0010 10z" />
  </Svg>
);

export const IconSun = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </Svg>
);

export const IconChevronDown = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 9l6 6 6-6" />
  </Svg>
);

export const IconChevronRight = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 6l6 6-6 6" />
  </Svg>
);

export const IconExternal = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 4h6v6" />
    <path d="M20 4l-9 9" />
    <path d="M18 14v5a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h5" />
  </Svg>
);

export const IconLock = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 018 0v4" />
  </Svg>
);

export const IconSearch = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </Svg>
);

export const IconPlus = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);

export const IconClose = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 6l12 12M18 6L6 18" />
  </Svg>
);

export const IconInfo = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </Svg>
);

export const IconCheckCircle = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12.5l2.5 2.5L16 9.5" />
  </Svg>
);

export const IconShield = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3l8 4v5c0 5-3.4 8.2-8 9-4.6-.8-8-4-8-9V7l8-4z" />
    <path d="M9 12l2 2 4-4" />
  </Svg>
);

export const IconDocument = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8l-5-5z" />
    <path d="M14 3v5h5" />
    <path d="M9 14h6M9 17h4" />
  </Svg>
);

export const IconMail = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 8l9 5 9-5" />
  </Svg>
);

export const IconSignOut = (p: IconProps) => (
  <Svg {...p}>
    <path d="M15 17l5-5-5-5" />
    <path d="M20 12H9" />
    <path d="M12 21H6a2 2 0 01-2-2V5a2 2 0 012-2h6" />
  </Svg>
);

/** Artwork supplied for a tool, served from public/brand/tools. */
const toolImages: Record<string, string> = {
  rebarfix: '/brand/tools/rebarfix.webp',
  bimhub: '/brand/tools/bimhub.webp',
};

/** Artwork for a tool card or hero. A tool with supplied artwork shows it;
 *  any other gets a placeholder drawn from its key, so each stays
 *  distinguishable until real artwork exists. */
export function ToolArt({ toolKey, width = 150, height = 66 }: { toolKey: string; width?: number; height?: number }) {
  const image = toolImages[toolKey];
  if (image) {
    return <img src={image} width={width} height={height} alt="" className="tool-card-art" />;
  }
  return (
    <svg width={width} height={height} viewBox="0 0 150 66" fill="none" className="tool-card-art" aria-hidden="true">
      <rect x="20" y="10" width="110" height="46" rx="2" fill="var(--ejot-light-grey)" stroke="var(--ejot-grey)" strokeWidth="1" />
      <path d="M32 46l22-24 16 16 12-14 20 22" stroke="var(--ejot-grey)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
